// src/components/notifications/NotificationBell.jsx
import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { Bell, Check, CheckCheck, ExternalLink } from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { useNotifications, useUnreadCount, useMarkRead, useMarkAllRead } from '@/hooks/useNotifications'
import { usePrivateChannel } from '@/hooks/useChannel'
import useAuthStore from '@/store/authStore'

// Map notification types to colors and labels
const TYPE_CONFIG = {
  assignment_due:    { color: 'bg-blue-100 text-blue-600',   label: 'Assignment' },
  test_reminder:     { color: 'bg-purple-100 text-purple-600', label: 'Exam' },
  fee_overdue:       { color: 'bg-red-100 text-red-600',     label: 'Fee' },
  attendance_alert:  { color: 'bg-orange-100 text-orange-600', label: 'Attendance' },
  grade_published:   { color: 'bg-green-100 text-green-600', label: 'Grade' },
  announcement:      { color: 'bg-indigo-100 text-indigo-600', label: 'Announcement' },
  quiz_live:         { color: 'bg-pink-100 text-pink-600',   label: 'Quiz' },
  achievement:       { color: 'bg-yellow-100 text-yellow-700', label: 'Achievement' },
  system:            { color: 'bg-gray-100 text-gray-600',   label: 'System' },
}

function timeAgo(dateStr) {
  const now = new Date()
  const date = new Date(dateStr)
  const diff = Math.floor((now - date) / 1000)

  if (diff < 60) return 'Just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`
  return date.toLocaleDateString()
}

export default function NotificationBell() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)

  const { data: unreadCount = 0 } = useUnreadCount()
  const { data: notificationsData } = useNotifications({ per_page: 5 })
  const markRead = useMarkRead()
  const markAllRead = useMarkAllRead()

  const notifications = notificationsData?.data || []

  // Subscribe to real-time notifications via WebSocket
  const handleRealtimeNotification = useCallback((data) => {
    // Invalidate queries so the list and count refresh instantly
    queryClient.invalidateQueries('notifications')
    queryClient.invalidateQueries('notifications-unread-count')

    // Show a toast for the incoming notification
    toast(data.title, {
      icon: '\uD83D\uDD14',
      duration: 4000,
    })
  }, [queryClient])

  usePrivateChannel(
    user ? `App.Models.User.${user.id}` : null,
    'NotificationCreated',
    handleRealtimeNotification
  )

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  // Close on Escape key
  useEffect(() => {
    function handleEscape(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    if (open) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [open])

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.is_read) {
      markRead.mutate(notification.id)
    }

    // Navigate if action_url exists
    const actionUrl = notification.data?.action_url
    if (actionUrl) {
      navigate(actionUrl)
    }

    setOpen(false)
  }

  const handleMarkAllRead = (e) => {
    e.stopPropagation()
    markAllRead.mutate()
  }

  const handleViewAll = () => {
    setOpen(false)
    navigate('/notifications')
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-lg hover:bg-gray-100"
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      <div
        className={clsx(
          'absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50',
          'transition-all duration-200 origin-top-right',
          open
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">{t('notifications.title')}</h3>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              disabled={markAllRead.isLoading}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors disabled:opacity-50"
            >
              <CheckCheck size={14} />
              {t('notifications.markAllRead')}
            </button>
          )}
        </div>

        {/* Notification List */}
        <div className="max-h-[360px] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="py-10 text-center">
              <Bell size={32} className="mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">{t('notifications.noNotifications')}</p>
            </div>
          ) : (
            notifications.map((notification) => {
              const config = TYPE_CONFIG[notification.type] || TYPE_CONFIG.system
              return (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={clsx(
                    'w-full flex items-start gap-3 px-4 py-3 text-left transition-colors border-b border-gray-50 last:border-b-0',
                    notification.is_read
                      ? 'bg-white hover:bg-gray-50'
                      : 'bg-blue-50/50 hover:bg-blue-50'
                  )}
                >
                  {/* Type badge */}
                  <span className={clsx(
                    'flex-shrink-0 mt-0.5 text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide',
                    config.color
                  )}>
                    {config.label}
                  </span>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className={clsx(
                      'text-sm leading-snug',
                      notification.is_read ? 'text-gray-600' : 'text-gray-900 font-medium'
                    )}>
                      {notification.title}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">
                      {notification.message}
                    </p>
                    <p className="text-[10px] text-gray-300 mt-1">
                      {timeAgo(notification.created_at)}
                    </p>
                  </div>

                  {/* Unread dot */}
                  {!notification.is_read && (
                    <span className="flex-shrink-0 w-2 h-2 mt-2 bg-blue-500 rounded-full" />
                  )}
                </button>
              )
            })
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100">
          <button
            onClick={handleViewAll}
            className="w-full flex items-center justify-center gap-1.5 px-4 py-3 text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors rounded-b-xl"
          >
            <ExternalLink size={14} />
            {t('notifications.viewAll')}
          </button>
        </div>
      </div>
    </div>
  )
}
